import React, { useState, useCallback } from 'react';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ValueMapping, FieldInfo } from '../../models';
import { Input, Select, SelectOption } from '../ui';
import { GripVertical, Trash2 } from '../ui/icons';

interface ValueMappingItemProps {
  mapping: ValueMapping;
  index: number;
  field: FieldInfo;
  onPatternChange: (index: number, pattern: string) => void;
  onValueChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

const ValueMappingItem: React.FC<ValueMappingItemProps> = React.memo(({
  mapping,
  index,
  field,
  onPatternChange,
  onValueChange,
  onRemove,
}) => {
  const [localPattern, setLocalPattern] = useState(mapping.pattern);
  const [localValue, setLocalValue] = useState(mapping.value);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mapping.pattern + index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handlePatternChange = useCallback((value: string) => {
    setLocalPattern(value);
    onPatternChange(index, value);
  }, [index, onPatternChange]);

  const handleValueChange = useCallback((value: string) => {
    setLocalValue(value);
    onValueChange(index, value);
  }, [index, onValueChange]);

  // Update local state when mapping prop changes
  React.useEffect(() => {
    setLocalPattern(mapping.pattern);
    setLocalValue(mapping.value);
  }, [mapping.pattern, mapping.value]);

  const valueOptions: SelectOption[] = Object.entries(field.values || {}).map(([value, label]) => ({
    value,
    label: String(label)
  }));

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 mb-2 border border-gray-300 rounded-lg cursor-grab active:cursor-grabbing bg-white"
    >
      <div className="grid grid-cols-12 gap-4 items-center">
        {/* Drag Handle */}
        <div className="col-span-1">
          <button
            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
            title="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </div>

        {/* Index Badge */}
        <div className="col-span-1">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-primary-500 text-primary-600 bg-primary-50">
            {index + 1}
          </span>
        </div>

        {/* Pattern Input */}
        <div className="col-span-4">
          <Input
            label="Pattern"
            value={localPattern}
            onChange={(e) => handlePatternChange((e.target as HTMLInputElement).value)}
            fullWidth
            placeholder="e.g., sale|sales"
          />
        </div>

        {/* Value Select */}
        <div className="col-span-5">
          <Select
            value={localValue}
            onChange={(e) => handleValueChange(e.target.value)}
            label="Value"
            options={valueOptions}
            fullWidth
          />
        </div>

        {/* Delete Button */}
        <div className="col-span-1">
          <button
            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
            onClick={() => onRemove(index)}
            title="Remove mapping"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
});

ValueMappingItem.displayName = 'ValueMappingItem';

export default ValueMappingItem; 